/**
 * TetherCam の一覧（/shoot/devices。Issue #789 → #790）: 補正サーバーへ状態（TetherCam API v4 の /state）を送ってきたデバイス（iPhone・Mac・OBS。
 * 区別しない）と、本番に設定を保存したデバイス（GetTetherCamDevices）を名乗った名前で合わせて並べる。開くとデバイスの設定（pages/TetherCamDevice.js）。
 * 状態は補正サーバーの GET /live/devices.json を 2 秒ごと。形の正本は tethercam/docs/tethercam-api.md §3。
 * 下に、補正サーバーが今作っている絵（メインの縦積みと席ごとの配信の動画）・座標が無い理由（/live/info.json の cams_missing）・
 * 画像認識（components/RecogPanel.js）・音声認識の文字（components/SpeechPanel.js）も出す（撮影の設定の画面を消したときに移した。Issue #801）。
 * fetch を持たない環境（テストの jsdom）では読まない。fetchImpl で差し替えられる。
 */
import { liveUrl } from '../components/liveBase';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/api';
import { usePageTitle } from '../appInfo';
import LiveStream from '../components/LiveStream';
import ShootNav from '../components/ShootNav';
import RecogPanel from '../components/RecogPanel';
import SpeechPanel from '../components/SpeechPanel';
import { deviceText, deviceWarn } from '../components/LiveDevicesPanel';
import { useLiveInfo } from '../components/liveCorrect';

export const POLL_MS = 2000;
const PLATFORMS = { ios: 'iPhone / iPad', mac: 'Mac', windows: 'Windows', obs: 'OBS' };
const FEATURES = {
  record: '録画', stream: '配信', upload: 'アップロード', crops: 'ゾーンの切り抜き', speech: '音声認識',
  speech_mic: '内蔵マイクで認識', speech_pull: '外部ストリーミングの受信', speech_listen: 'ストリーミング待機', audio_out: '音を流す', power: '電池と熱',
};
export const SPEECH_SOURCES = { mic: '内蔵マイク', pull: '外部ストリーミングの受信', listen: 'ストリーミング待機' };
/** 補正サーバーの受け口（SRT の口。名前は補正サーバーの inputs の鍵） */
const INPUT_LABELS = { obs: 'OBS', tc1: 'TetherCam 1', tc2: 'TetherCam 2' };
export const inputLabel = (name) => INPUT_LABELS[name] ?? name;

/** 補正サーバーが座標を使えない理由（/live/info.json・/live/cams.json の cams_missing。Issue #801）→ 一言。無ければ null。
 * 文字列・配列・席ごとの {席: 理由} のどれでも読む */
export function camsMissingText(m) {
  if (!m) return null;
  if (typeof m === 'string') return m;
  if (Array.isArray(m)) return m.length ? m.map(String).join('・') : null;
  if (typeof m === 'object') {
    const parts = Object.entries(m).filter(([, v]) => v).map(([k, v]) => `${/^\d+$/.test(k) ? `P${k}` : k}: ${v}`);
    return parts.length ? parts.join('・') : null;
  }
  return String(m);
}


function mb(bytes) {
  return typeof bytes === 'number' ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : '';
}

/** 1 台ぶんの表の行 [[見出し, 中身]]。無い項目は出さない */
export function deviceRows(d) {
  const st = d?.state ?? {};
  const dev = st.device ?? {};
  const rows = [];
  const app = dev.app ? `${dev.app.version ?? ''}${dev.app.build ? `（${dev.app.build}）` : ''}${dev.app.obs_version ? ` / OBS ${dev.app.obs_version}` : ''}` : null;
  if (app) rows.push(['アプリ', app]);
  if (dev.os) rows.push(['OS', `${dev.os.name ?? ''} ${dev.os.version ?? ''}`.trim()]);
  if (dev.model) rows.push(['機種', dev.model]);
  const input = d?.input;
  if (input) {
    rows.push(['補正サーバーの受け口', `${inputLabel(input.name)}: ${input.fresh ? `絵が届いています${input.size ? `（${input.size[0]}×${input.size[1]}）` : ''}` : '絵は届いていません'}`]);
  }
  if (st.stream) {
    const s = st.stream;
    const size = s.width && s.height ? `・${s.width}×${s.height}${s.fps ? ` ${s.fps}fps` : ''}` : '';
    rows.push(['配信', `${s.active ? (s.connected ? '送っています' : '送ろうとしています（届いていない）') : '止まっています'}${size}${s.url ? `・${s.url}` : ''}`]);
  }
  rows.push(['録画', st.recording?.key ? `録画中: ${st.recording.key}` : 'していません']);
  if (st.finished?.key) {
    const v = st.finished.video;
    rows.push(['アップロード待ち', `${st.finished.key}${st.finished.size ? `（${mb(st.finished.size)}` : ''}${v ? `・${v.width}×${v.height}` : ''}${st.finished.size ? '）' : ''}`]);
  }
  if (st.uploaded?.key) rows.push(['上げたパート', `${st.uploaded.key}: ${st.uploaded.parts ?? 0} 個`]);
  if (st.crops) rows.push(['ゾーンの切り抜き', `四角形 ${st.crops.regions ?? 0}・送った ${st.crops.sent ?? 0}・失敗 ${st.crops.failed ?? 0}`]);
  if (st.speech) {
    const sp = st.speech;
    let text = sp.enabled ? `${sp.active ? '動いています' : '止まっています'}・${SPEECH_SOURCES[sp.source] ?? sp.source ?? ''}` : '使っていません';
    if (sp.enabled && sp.source === 'pull' && sp.pull) text += `・${sp.pull.url || '（URL 無し）'}・${sp.pull.connected ? 'つながっています' : `つながっていません${sp.pull.reason ? `（${sp.pull.reason}）` : ''}`}`;
    if (sp.enabled && sp.source === 'listen' && sp.listen) text += `・${sp.listen.url ?? ''}・${sp.listen.receiving ? `届いています${sp.listen.from ? `（${sp.listen.from} から）` : ''}` : '届いていません'}`;
    rows.push(['音声認識', text]);
  }
  if (st.audio_sources?.length) rows.push(['流せる音', st.audio_sources.map((a) => a.name || a.id).join('・')]);
  if (st.audio_out?.length) {
    rows.push(['流している音', st.audio_out.map((a) => `${a.source ?? a.stream_id} → ${a.url}（${a.connected ? 'つながっています' : `つながっていません${a.reason ? `: ${a.reason}` : ''}`}）`).join(' / ')]);
  }
  const power = st.power ? deviceText({ ...st.power, age_sec: d.age_sec }) : null;
  if (power) rows.push(['電池と熱', power]);
  return rows;
}

/** 見出しの一言: 名前・種類・チャンネル・接続 */
export function deviceTitle(d) {
  const dev = d?.state?.device ?? {};
  const kind = PLATFORMS[dev.platform] ?? dev.platform ?? (d?.source === 'obs' ? 'OBS' : 'TetherCam');
  return `${dev.name || kind}（${kind}・チャンネル ${d?.channel ?? '?'}）`;
}

/** デバイスの今の状態（v4 の状態そのまま）。一覧とデバイスのページで使う */
export function DeviceCard({ d, title = null }) {
  const features = Object.entries(d.state?.features ?? {});
  const warn = deviceWarn({ ...(d.state?.power ?? {}) });
  return (
    <section className="card" data-testid={`device-${d.source}`} style={{ display: 'grid', gap: 8, minWidth: 0, opacity: d.connected ? 1 : 0.6 }}>
      <h3 className="card-title" style={{ margin: 0 }}>{title ?? deviceTitle(d)}</h3>
      <div data-testid={`device-${d.source}-seen`} style={{ fontSize: 13, color: !d.connected ? '#ff8a8a' : warn ? '#f0c36b' : '#9be7a1' }}>
        {d.connected ? '接続中' : '切れています'}（最後の状態 {d.age_sec} 秒前・API v{d.api}）
      </div>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
        <tbody>
          {deviceRows(d).map(([k, v]) => (
            <tr key={k}>
              <td style={{ padding: '3px 8px 3px 0', whiteSpace: 'nowrap', color: '#a0a0c0', verticalAlign: 'top' }}>{k}</td>
              <td style={{ padding: '3px 0', wordBreak: 'break-all' }}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {features.length ? (
        <div data-testid={`device-${d.source}-features`} style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {features.map(([k, f]) => {
            const ok = f?.supported && f?.available;
            const label = FEATURES[k] ?? k;
            return (
              <span key={k} title={f?.reason ?? ''} style={{ fontSize: 12, padding: '2px 8px', borderRadius: 10,
                border: `1px solid ${ok ? '#4a7' : f?.supported ? '#a84' : '#555'}`, color: ok ? '#9be7a1' : f?.supported ? '#f0c36b' : '#8a93a8' }}>
                {label}{ok ? '' : f?.supported ? `: ${f?.reason ?? '使えない'}` : ': 無い'}
              </span>
            );
          })}
        </div>
      ) : null}
      <details>
        <summary style={{ fontSize: 12, color: '#8a93a8', cursor: 'pointer' }}>届いた状態（JSON）</summary>
        <pre style={{ fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0 }}>{JSON.stringify(d.state, null, 2)}</pre>
      </details>
    </section>
  );
}

/** 名乗った名前（無ければ出どころ） */
export const deviceName = (d) => d?.state?.device?.name || d?.source;

/** 補正サーバーの /live/devices.json を 2 秒ごと。undefined = 読み込み中、null = 届かない */
export function useLiveDevices(fetchImpl) {
  const [devices, setDevices] = useState(undefined);
  useEffect(() => {
    const f = fetchImpl ?? (typeof fetch === 'function' && !navigator.userAgent.includes('jsdom') ? fetch : null);
    if (!f) return undefined;
    let alive = true;
    let timer;
    const poll = async () => {
      let next = null;
      try {
        const r = await f(liveUrl('/live/devices.json'), { cache: 'no-store' });
        next = r.ok ? (await r.json()).devices ?? [] : null;
      } catch { next = null; }
      if (!alive) return;
      setDevices(next);
      timer = setTimeout(poll, POLL_MS);
    };
    poll();
    return () => { alive = false; clearTimeout(timer); };
  }, [fetchImpl]);
  return devices;
}

/** 本番に保存したデバイスの設定 [{name, doc, updated_at}]。undefined = 読み込み中 */
export function useSavedDevices() {
  const [saved, setSaved] = useState(undefined);
  const reload = async () => {
    try {
      setSaved((await api.project('GetTetherCamDevices', {}))?.devices ?? []);
    } catch { setSaved([]); }
  };
  useEffect(() => { reload(); }, []);
  return [saved, reload];
}

/** 接続中（と過去に状態が届いた）デバイスと、保存したデバイスを名前で合わせる。接続中 → 切れている → 保存だけ の順 */
export function mergeDevices(live, saved) {
  const by = new Map();
  (live ?? []).forEach((d) => by.set(deviceName(d), { name: deviceName(d), live: d, saved: null }));
  (saved ?? []).forEach((s) => by.set(s.name, { ...(by.get(s.name) ?? { name: s.name, live: null }), saved: s }));
  const rank = (r) => (r.live?.connected ? 0 : r.live ? 1 : 2);     // 接続中 → 切れている → 保存だけ
  return [...by.values()].sort((a, b) => (rank(a) - rank(b)) || a.name.localeCompare(b.name));
}

/** 保存した設定の一言 */
export function savedSummary(doc) {
  if (!doc) return '設定はまだ保存していません';
  const parts = [];
  const n = doc.entries?.length ?? 0;
  if (n) parts.push(`座標 ${n} 枠`);
  if (doc.speech) parts.push(`音声認識 ${doc.speech.enabled === false ? 'オフ' : SPEECH_SOURCES[doc.speech.source] ?? doc.speech.source}`);
  return parts.length ? parts.join('・') : '設定は空です';
}

export default function TetherCamDevices({ fetchImpl = null }) {
  usePageTitle('TetherCam');
  const devices = useLiveDevices(fetchImpl);
  const [saved] = useSavedDevices();
  const rows = mergeDevices(devices, saved);
  const live = !fetchImpl;
  const infoFetch = fetchImpl ?? (typeof fetch === 'function' && !navigator.userAgent.includes('jsdom') ? fetch : null);
  const { info } = useLiveInfo(infoFetch, POLL_MS);
  const missing = camsMissingText(info?.cams_missing);
  return (
    <>
    <ShootNav />
    <div className="page" data-testid="tethercam-devices">
      <div className="page-header"><h2>TetherCam</h2></div>
      <p style={{ fontSize: 14, lineHeight: 1.7, margin: 0 }}>
        補正サーバーへ状態を送ってきたデバイス（iPhone・Mac・OBS）と、設定を保存したデバイスの一覧です。開くと、そのデバイスの絵の上の座標（2 席ぶん）・
        ゾーンの確認・音声認識の経路を決めて本番に保存できます（デバイスが名乗る名前ごと）。
      </p>
      {missing ? (
        <div data-testid="cams-missing" style={{ color: '#f0c36b', fontSize: 13 }}>
          座標がありません: {missing}（収録の設定でライブの P1 / P2 のデバイスを選び、そのデバイスの座標を保存してください）
        </div>
      ) : null}
      {devices === null ? <div data-testid="devices-down" style={{ color: '#ff8a8a', fontSize: 13 }}>補正サーバーに届きません（起きていないか、止まっています）</div> : null}
      {devices !== undefined && saved !== undefined && !rows.length ? <div data-testid="devices-empty" style={{ fontSize: 13, color: '#b8c0d4' }}>まだどの TetherCam からも状態が届いていません（TetherCam の「シンクロ連携」に制御 URL を入れると届きます）</div> : null}
      <div style={{ display: 'grid', gap: 8 }}>
        {rows.map((r) => (
          <Link key={r.name} to={`/shoot/devices/${encodeURIComponent(r.name)}`} data-testid={`device-row-${r.name}`} className="card"
            style={{ display: 'grid', gap: 4, textDecoration: 'none', color: 'inherit', opacity: r.live?.connected ? 1 : 0.7 }}>
            <strong>{r.live ? deviceTitle(r.live) : r.name}</strong>
            <span style={{ fontSize: 13, color: r.live?.connected ? '#9be7a1' : '#b8c0d4' }}>
              {r.live ? (r.live.connected ? '接続中' : `切れています（${r.live.age_sec} 秒前）`) : '補正サーバーには来ていません'}・{savedSummary(r.saved?.doc)}
            </span>
          </Link>
        ))}
      </div>
      <section className="card" data-testid="main-stream" style={{ display: 'grid', gap: 12, minWidth: 0 }}>
        <h3 className="card-title" style={{ margin: 0 }}>補正サーバーの配信</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, alignItems: 'start' }}>
          <LiveStream url="/live/stream?preview=1" live={live} testId="main-video" maxWidth={360}
            label="メイン（フィールド動画と同じ縦積み。収録の設定で選んだデバイスの座標から）" />
          {[1, 2].map((seat) => (
            <LiveStream key={seat} url={`/live/stream?seat=${seat}`} live={live} testId={`seat-${seat}-video`}
              label={`P${seat} の席ごとの配信（手札＋認識領域）`} />
          ))}
        </div>
      </section>
      <RecogPanel />
      <SpeechPanel />
    </div>
    </>
  );
}
